/**
 * The Workspace board's state: where each widget sits, which are hidden or
 * collapsed, each widget's view, the divider position, and the edit session.
 * Every change goes through `boardReducer` so the page, the prefs sync and
 * the tests all see the same rules.
 */
import {
  COLUMNS,
  DEFAULT_HIDDEN,
  MIN_SIZE,
  DEFAULT_LAYOUT,
  WIDGET_IDS,
  addWidget,
  compact,
  fittedLayout,
  moveWidget,
  resizeWidget,
  type Cell,
  type Flags,
  type Geometry,
  type Layout,
  type WidgetId,
} from './grid';

export type WidgetView = 'list' | 'tiles' | 'card';
export type ListWidgetId = Exclude<WidgetId, 'search'>;
export type Views = Record<ListWidgetId, WidgetView>;

export const DEFAULT_VIEWS: Views = { activity: 'list', feed: 'list', active: 'list', pinned: 'tiles' };

/** Widgets that exist in this release. */
export const AVAILABLE_WIDGETS: ReadonlySet<WidgetId> = new Set(['search', 'activity', 'feed', 'active', 'pinned']);

/**
 * Saved-board version. Boards saved before 2 (R2) had Feed hidden because it
 * hadn't shipped; loading one brings Feed in once.
 */
export const BOARD_VERSION = 2;

/** What is saved per person (crm_workspace_preferences.layout). */
export interface SavedBoard {
  layout: Layout;
  hidden: Flags;
  collapsed: Flags;
  views: Views;
  leftW: number | null;
  /** True until the person edits the layout: the board follows the zone size. */
  auto: boolean;
  v?: number;
}

export interface Board extends SavedBoard {
  editing: boolean;
  /** The board as it was when Edit started; Cancel restores it. */
  snapshot: SavedBoard | null;
}

export type BoardAction =
  | { type: 'fit'; geometry: Geometry }
  | { type: 'move'; id: WidgetId; to: { x: number; y: number } }
  | { type: 'resize'; id: WidgetId; size: { w: number; h: number } }
  | { type: 'remove'; id: WidgetId }
  | { type: 'add'; id: WidgetId }
  | { type: 'toggleCollapse'; id: WidgetId }
  | { type: 'setView'; id: ListWidgetId; view: WidgetView }
  | { type: 'setLeftW'; width: number | null }
  | { type: 'enterEdit' }
  | { type: 'cancelEdit' }
  | { type: 'doneEdit' }
  | { type: 'reset'; geometry: Geometry }
  | { type: 'load'; saved: SavedBoard };

export function initialBoard(): Board {
  return {
    layout: DEFAULT_LAYOUT,
    hidden: { ...DEFAULT_HIDDEN },
    collapsed: {},
    views: { ...DEFAULT_VIEWS },
    leftW: null,
    auto: true,
    editing: false,
    snapshot: null,
  };
}

/** The part of the board that is saved. */
export function savedPart(b: Board): SavedBoard {
  return { layout: b.layout, hidden: b.hidden, collapsed: b.collapsed, views: b.views, leftW: b.leftW, auto: b.auto, v: BOARD_VERSION };
}

export function boardReducer(b: Board, action: BoardAction): Board {
  switch (action.type) {
    case 'fit':
      return b.auto && !b.editing ? { ...b, layout: compact(fittedLayout(action.geometry), b.hidden, b.collapsed) } : b;
    case 'move':
      return { ...b, layout: moveWidget(b.layout, action.id, action.to, b.hidden, b.collapsed) };
    case 'resize': {
      const layout = resizeWidget(b.layout, action.id, action.size, b.hidden, b.collapsed);
      return layout === b.layout ? b : { ...b, layout };
    }
    case 'remove': {
      const hidden = { ...b.hidden, [action.id]: true };
      return { ...b, hidden, layout: compact(b.layout, hidden, b.collapsed) };
    }
    case 'add': {
      if (!AVAILABLE_WIDGETS.has(action.id) || !b.hidden[action.id]) return b;
      return { ...b, ...addWidget(b.layout, action.id, b.hidden, b.collapsed) };
    }
    case 'toggleCollapse': {
      const collapsed = { ...b.collapsed, [action.id]: !b.collapsed[action.id] };
      return { ...b, collapsed, layout: compact(b.layout, b.hidden, collapsed) };
    }
    case 'setView':
      return { ...b, views: { ...b.views, [action.id]: action.view } };
    case 'setLeftW':
      return { ...b, leftW: action.width };
    case 'enterEdit':
      return b.editing ? b : { ...b, editing: true, snapshot: savedPart(b) };
    case 'cancelEdit':
      return b.snapshot ? { ...b, ...b.snapshot, editing: false, snapshot: null } : { ...b, editing: false };
    case 'doneEdit':
      return { ...b, editing: false, snapshot: null, auto: false };
    case 'reset':
      return {
        ...b,
        layout: compact(fittedLayout(action.geometry), DEFAULT_HIDDEN, {}),
        hidden: { ...DEFAULT_HIDDEN },
        collapsed: {},
        views: { ...DEFAULT_VIEWS },
        leftW: null,
        auto: true,
      };
    case 'load':
      return { ...b, ...action.saved, editing: false, snapshot: null };
    default:
      return b;
  }
}

/** Widgets that can be added back right now (hidden and available). */
export function addableWidgets(b: Board): WidgetId[] {
  return WIDGET_IDS.filter((id) => b.hidden[id] && AVAILABLE_WIDGETS.has(id));
}

const ZONE_MIN = 460;
const CHAT_MIN = 620;

/** Width of the widget zone: the dragged width or the default, never squeezing the chat. */
export function leftZoneWidth(dragged: number | null, rowWidth: number): number {
  const max = Math.max(ZONE_MIN, rowWidth - CHAT_MIN);
  if (dragged == null) return Math.min(Math.max(760, Math.round(rowWidth * 0.52)), max);
  return Math.min(Math.max(ZONE_MIN, dragged), rowWidth - CHAT_MIN);
}

const VIEWS: ReadonlySet<string> = new Set(['list', 'tiles', 'card']);

function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function parseCell(id: WidgetId, raw: unknown): Cell | null {
  if (!isObject(raw)) return null;
  const nums = ['x', 'y', 'w', 'h'].map((k) => raw[k]);
  if (!nums.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  const [x = 0, y = 0, w = 0, h = 0] = (nums as number[]).map(Math.round);
  const min = MIN_SIZE[id];
  const width = Math.min(Math.max(min.w, w), COLUMNS);
  return {
    x: Math.min(Math.max(0, x), COLUMNS - width),
    y: Math.max(0, y),
    w: width,
    h: min.fixedH ?? Math.max(min.h, h),
  };
}

function parseFlags(raw: unknown): Flags {
  const out: Flags = {};
  if (!isObject(raw)) return out;
  for (const id of WIDGET_IDS) if (raw[id] === true) out[id] = true;
  return out;
}

/**
 * A board saved on the server (or by an older build). Anything malformed in
 * the layout means "use the default board"; small problems are repaired.
 */
export function parseSavedBoard(raw: unknown): SavedBoard | null {
  if (!isObject(raw) || !isObject(raw.layout)) return null;
  const layout = {} as Layout;
  for (const id of WIDGET_IDS) {
    const cell = parseCell(id, (raw.layout as Record<string, unknown>)[id]);
    if (!cell) return null;
    layout[id] = cell;
  }
  let hidden = parseFlags(raw.hidden);
  for (const id of WIDGET_IDS) if (!AVAILABLE_WIDGETS.has(id)) hidden[id] = true;
  const collapsed = parseFlags(raw.collapsed);
  let placed = layout;
  if (raw.v !== BOARD_VERSION && hidden.feed) {
    // Saved before Feed shipped: bring it in once (an auto board is re-fitted anyway).
    const added = addWidget(layout, 'feed', hidden, collapsed);
    placed = added.layout;
    hidden = added.hidden;
  }
  const views = { ...DEFAULT_VIEWS };
  if (isObject(raw.views)) {
    for (const id of Object.keys(views) as ListWidgetId[]) {
      const v = raw.views[id];
      if (typeof v === 'string' && VIEWS.has(v)) views[id] = v as WidgetView;
    }
  }
  const leftW = typeof raw.leftW === 'number' && Number.isFinite(raw.leftW) ? Math.round(raw.leftW) : null;
  return {
    layout: compact(placed, hidden, collapsed),
    hidden,
    collapsed,
    views,
    leftW,
    auto: raw.auto !== false,
    v: BOARD_VERSION,
  };
}
